import React, { useEffect, useState, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  TextInput,
  ActivityIndicator,
  Modal,
  RefreshControl,
} from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import { COLORS, FONTS } from '../../constants/config';
import { useNotification } from '../../contexts/NotificationContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { PageContainer, Breadcrumb } from '../../components';
import MultiSelectDropdown from '../../components/MultiSelectDropdown';
import {
  calibrationMeetingsApi,
  CalibrationMeeting,
  CalibrationMeetingType,
  CalibrationMeetingStatus,
  organizationalUnitsApi,
} from '../../services/apiService';

/**
 * اجتماعات المتابعة الدورية — DTS UC-06.
 *
 * Owned by فريق الاستراتيجية. They schedule recurring calibration meetings
 * (quarterly / half-yearly / three-quarter / year-end) where invited
 * sections walk through their KPI progress; the meeting record carries the
 * agenda, notes, and recorded decisions. Other roles can view; Strategy
 * Team has full CRUD per the DTS permissions matrix.
 */

const MEETING_TYPE_LABELS: Record<CalibrationMeetingType, string> = {
  quarterly: 'ربعي',
  half_yearly: 'نصف سنوي',
  three_quarter: 'ثلاثة أرباع',
  year_end: 'نهاية الدورة',
};

const STATUS_LABELS: Record<CalibrationMeetingStatus, string> = {
  scheduled: 'مجدول',
  in_progress: 'قيد الانعقاد',
  completed: 'منعقد',
  cancelled: 'ملغي',
};

const STATUS_COLORS: Record<CalibrationMeetingStatus, { bg: string; fg: string }> = {
  scheduled:   { bg: '#EFF6FF', fg: '#1E40AF' },
  in_progress: { bg: '#FEF3C7', fg: '#92400E' },
  completed:   { bg: '#DCFCE7', fg: '#15803D' },
  cancelled:   { bg: '#FEE2E2', fg: '#B91C1C' },
};

export default function CalibrationMeetingsScreen() {
  const { showSuccess, showError } = useNotification();
  const { colors } = useTheme();
  const { user } = useAuth();

  // DTS 2.2 "اجتماعات المتابعة الدورية": Strategy Team = إنشاء وإدارة,
  // Section Head + Director = مشاركة (read-only), DG = اطلاع. The
  // semantically correct gate is hrcpms.calibration.manage (held only by
  // STRATEGY_TEAM_PERMISSIONS). Section Head's
  // hrcpms.calibration.participate keeps them on the list but hides
  // create/edit affordances.
  const canEdit = !!(
    user?.is_superuser
    || (user?.permissions || []).includes('hrcpms.calibration.manage')
  );

  const [items, setItems] = useState<CalibrationMeeting[]>([]);
  const [units, setUnits] = useState<{ id: number; name: string; name_ar?: string | null }[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Form state
  const [editing, setEditing] = useState<CalibrationMeeting | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [formTitle, setFormTitle] = useState('');
  const [formType, setFormType] = useState<CalibrationMeetingType>('quarterly');
  const [formDate, setFormDate] = useState('');     // YYYY-MM-DD
  const [formTime, setFormTime] = useState('');     // HH:mm
  const [formAgenda, setFormAgenda] = useState('');
  const [formNotes, setFormNotes] = useState('');
  const [formDecisions, setFormDecisions] = useState('');
  const [formInvitedUnits, setFormInvitedUnits] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  // Detail expand toggle (per row)
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const fetchData = useCallback(async () => {
    try {
      const [meetingsResp, unitsResp] = await Promise.all([
        calibrationMeetingsApi.list({ page_size: 200 }),
        organizationalUnitsApi.list({ page_size: 9999 }),
      ]);
      setItems(meetingsResp.results || []);
      setUnits(((unitsResp.results || []) as any[]).map((u) => ({ id: u.id, name: u.name, name_ar: u.name_ar })));
    } catch (err) {
      console.error('Failed to load calibration meetings:', err);
      showError('تعذّر تحميل اجتماعات المتابعة الدورية');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [showError]);

  useFocusEffect(useCallback(() => { fetchData(); }, [fetchData]));

  const openAdd = () => {
    setEditing(null);
    setFormTitle('');
    setFormType('quarterly');
    setFormDate('');
    setFormTime('');
    setFormAgenda('');
    setFormNotes('');
    setFormDecisions('');
    setFormInvitedUnits([]);
    setShowForm(true);
  };

  const openEdit = (item: CalibrationMeeting) => {
    setEditing(item);
    setFormTitle(item.title);
    setFormType(item.meeting_type);
    // scheduled_date arrives as ISO; split into date + time for the form
    const [datePart, timePart] = (item.scheduled_date || '').split('T');
    setFormDate(datePart || '');
    setFormTime(timePart ? timePart.substring(0, 5) : '');
    setFormAgenda(item.agenda || '');
    setFormNotes(item.meeting_notes || '');
    setFormDecisions(item.decisions || '');
    setFormInvitedUnits((item.invited_units || []).map(String));
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!formTitle.trim()) {
      showError('عنوان الاجتماع إجباري');
      return;
    }
    if (!formDate) {
      showError('تاريخ الاجتماع إجباري');
      return;
    }
    // الوقت is optional, but if entered it must be a valid 24-hour HH:MM —
    // otherwise the concatenated scheduled_date below is malformed. (type="time"
    // isn't reliably forwarded by RN-Web's TextInput, so the field can behave as
    // free text.)
    const time = (formTime || '').trim();
    if (time && !/^([01]\d|2[0-3]):[0-5]\d$/.test(time)) {
      showError('الوقت غير صالح — استخدم الصيغة HH:MM (مثال: 09:30)');
      return;
    }
    setSaving(true);
    try {
      // Combine date+time into ISO. Default time to 09:00 if user didn't set it.
      const scheduled = `${formDate}T${time || '09:00'}:00`;
      const payload: Partial<CalibrationMeeting> = {
        title: formTitle.trim(),
        meeting_type: formType,
        scheduled_date: scheduled,
        agenda: formAgenda.trim(),
        meeting_notes: formNotes.trim(),
        decisions: formDecisions.trim(),
        invited_units: formInvitedUnits.map((id) => parseInt(id, 10)),
      };
      if (editing) {
        await calibrationMeetingsApi.update(editing.id, payload);
        showSuccess('تم تحديث الاجتماع');
      } else {
        await calibrationMeetingsApi.create(payload);
        showSuccess('تم إضافة الاجتماع');
      }
      setShowForm(false);
      await fetchData();
    } catch (err: any) {
      showError(err?.response?.data?.detail || 'فشل الحفظ');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item: CalibrationMeeting) => {
    if (!window.confirm(`حذف اجتماع "${item.title}"؟`)) return;
    try {
      await calibrationMeetingsApi.delete(item.id);
      showSuccess('تم الحذف');
      await fetchData();
    } catch (err: any) {
      showError(err?.response?.data?.detail || 'فشل الحذف');
    }
  };

  const handleStart = async (item: CalibrationMeeting) => {
    try {
      await calibrationMeetingsApi.start(item.id);
      showSuccess('تم بدء الاجتماع');
      await fetchData();
    } catch (err: any) {
      showError(err?.response?.data?.detail || 'فشل بدء الاجتماع');
    }
  };

  const handleComplete = async (item: CalibrationMeeting) => {
    if (!window.confirm(`إغلاق اجتماع "${item.title}"؟ لن يمكن تعديله بعدها.`)) return;
    try {
      await calibrationMeetingsApi.complete(item.id);
      showSuccess('تم إغلاق الاجتماع');
      await fetchData();
    } catch (err: any) {
      showError(err?.response?.data?.detail || 'فشل الإغلاق');
    }
  };

  const handleCancel = async (item: CalibrationMeeting) => {
    if (!window.confirm(`إلغاء اجتماع "${item.title}"؟`)) return;
    try {
      await calibrationMeetingsApi.cancel(item.id);
      showSuccess('تم إلغاء الاجتماع');
      await fetchData();
    } catch (err: any) {
      showError(err?.response?.data?.detail || 'فشل الإلغاء');
    }
  };

  const themeColors = {
    card: colors.card,
    cardBorder: colors.cardBorder,
    textPrimary: colors.textPrimary,
    textMuted: colors.textMuted,
    inputBackground: colors.inputBackground,
    border: colors.border,
  };

  const formatDateTime = (iso: string | null) => {
    if (!iso) return '—';
    try {
      const d = new Date(iso);
      const date = d.toLocaleDateString('ar', { year: 'numeric', month: 'long', day: 'numeric' });
      const time = d.toLocaleTimeString('ar', { hour: '2-digit', minute: '2-digit' });
      return `${date} — ${time}`;
    } catch {
      return iso;
    }
  };

  return (
    <PageContainer>
      <ScrollView
        contentContainerStyle={{ padding: 16, gap: 16 }}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={() => { setRefreshing(true); fetchData(); }} />
        }
      >
        <Breadcrumb items={[
          { label: 'الرئيسية', icon: 'home' as any },
          { label: 'اجتماعات المتابعة الدورية' },
        ]} />

        <View style={[styles.headerCard, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }]}>
          <View style={{ flex: 1 }}>
            <Text style={[styles.title, { color: themeColors.textPrimary }]}>اجتماعات المتابعة الدورية</Text>
            <Text style={[styles.subtitle, { color: themeColors.textMuted }]}>
              ينظم فريق الاستراتيجية اجتماعات متابعة دورية مع الأقسام (ربعي/نصف/ثلاثة أرباع/نهاية الدورة) لمتابعة سير العمل وتعديل الخطط عند الحاجة.
            </Text>
          </View>
          {canEdit && (
            <TouchableOpacity style={styles.addButton} onPress={openAdd}>
              <Text style={styles.addButtonText}>+ إضافة اجتماع</Text>
            </TouchableOpacity>
          )}
        </View>

        {loading ? (
          <View style={{ padding: 40, alignItems: 'center' }}>
            <ActivityIndicator size="large" color={COLORS.primary} />
          </View>
        ) : items.length === 0 ? (
          <View style={[styles.emptyCard, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }]}>
            <Text style={[styles.emptyText, { color: themeColors.textMuted }]}>
              لا توجد اجتماعات متابعة دورية بعد
            </Text>
            {canEdit && (
              <Text style={[styles.emptyHint, { color: themeColors.textMuted }]}>
                ابدأ بإضافة اجتماع من زر "إضافة اجتماع" أعلاه.
              </Text>
            )}
          </View>
        ) : (
          <View style={{ gap: 12 }}>
            {items.map((item) => {
              const statusStyle = STATUS_COLORS[item.status];
              const isExpanded = expandedId === item.id;
              return (
                <View
                  key={item.id}
                  style={[styles.itemCard, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }]}
                >
                  <View style={{ flex: 1 }}>
                    <View style={styles.itemHeaderRow}>
                      <View style={[styles.statusBadge, { backgroundColor: statusStyle.bg }]}>
                        <Text style={[styles.statusBadgeText, { color: statusStyle.fg }]}>
                          {STATUS_LABELS[item.status]}
                        </Text>
                      </View>
                      <View style={styles.typeBadge}>
                        <Text style={styles.typeBadgeText}>
                          {MEETING_TYPE_LABELS[item.meeting_type]}
                        </Text>
                      </View>
                      <Text style={[styles.itemTitle, { color: themeColors.textPrimary }]}>
                        {item.title}
                      </Text>
                    </View>

                    <Text style={[styles.itemMeta, { color: themeColors.textMuted }]}>
                      {formatDateTime(item.scheduled_date)}
                    </Text>

                    {item.invited_unit_names && item.invited_unit_names.length > 0 && (
                      <Text style={[styles.itemMeta, { color: themeColors.textMuted, marginTop: 4 }]}>
                        الأقسام المدعوة: {item.invited_unit_names.join('، ')}
                      </Text>
                    )}

                    <TouchableOpacity onPress={() => setExpandedId(isExpanded ? null : item.id)}>
                      <Text style={styles.expandToggle}>
                        {isExpanded ? '▲ إخفاء التفاصيل' : '▼ عرض التفاصيل'}
                      </Text>
                    </TouchableOpacity>

                    {isExpanded && (
                      <View style={styles.detailBox}>
                        {item.agenda ? (
                          <View style={styles.detailGroup}>
                            <Text style={styles.detailLabel}>جدول الأعمال</Text>
                            <Text style={[styles.detailText, { color: themeColors.textPrimary }]}>{item.agenda}</Text>
                          </View>
                        ) : null}
                        {item.meeting_notes ? (
                          <View style={styles.detailGroup}>
                            <Text style={styles.detailLabel}>ملاحظات الاجتماع</Text>
                            <Text style={[styles.detailText, { color: themeColors.textPrimary }]}>{item.meeting_notes}</Text>
                          </View>
                        ) : null}
                        {item.decisions ? (
                          <View style={styles.detailGroup}>
                            <Text style={styles.detailLabel}>القرارات المتخذة</Text>
                            <Text style={[styles.detailText, { color: themeColors.textPrimary }]}>{item.decisions}</Text>
                          </View>
                        ) : null}
                        {!item.agenda && !item.meeting_notes && !item.decisions && (
                          <Text style={[styles.detailText, { color: themeColors.textMuted, fontStyle: 'italic' }]}>
                            لا توجد تفاصيل مدخلة بعد
                          </Text>
                        )}
                      </View>
                    )}
                  </View>

                  {canEdit && (
                    <View style={styles.actionsCol}>
                      {item.status === 'scheduled' && (
                        <TouchableOpacity style={styles.startBtn} onPress={() => handleStart(item)}>
                          <Text style={styles.startBtnText}>بدء</Text>
                        </TouchableOpacity>
                      )}
                      {(item.status === 'scheduled' || item.status === 'in_progress') && (
                        <TouchableOpacity style={styles.completeBtn} onPress={() => handleComplete(item)}>
                          <Text style={styles.completeBtnText}>إغلاق</Text>
                        </TouchableOpacity>
                      )}
                      {item.status !== 'completed' && item.status !== 'cancelled' && (
                        <TouchableOpacity style={styles.editBtn} onPress={() => openEdit(item)}>
                          <Text style={styles.editBtnText}>تعديل</Text>
                        </TouchableOpacity>
                      )}
                      {item.status !== 'completed' && item.status !== 'cancelled' && (
                        <TouchableOpacity style={styles.cancelMeetingBtn} onPress={() => handleCancel(item)}>
                          <Text style={styles.cancelMeetingBtnText}>إلغاء</Text>
                        </TouchableOpacity>
                      )}
                      <TouchableOpacity style={styles.deleteBtn} onPress={() => handleDelete(item)}>
                        <Text style={styles.deleteBtnText}>حذف</Text>
                      </TouchableOpacity>
                    </View>
                  )}
                </View>
              );
            })}
          </View>
        )}
      </ScrollView>

      <Modal visible={showForm} transparent animationType="fade" onRequestClose={() => setShowForm(false)}>
        <View style={styles.modalOverlay}>
          <View style={[styles.modalCard, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }]}>
            <ScrollView contentContainerStyle={{ paddingBottom: 8 }}>
              <Text style={[styles.modalTitle, { color: themeColors.textPrimary }]}>
                {editing ? 'تعديل اجتماع المتابعة الدورية' : 'إضافة اجتماع متابعة دورية'}
              </Text>

              <View style={{ gap: 12 }}>
                <View>
                  <Text style={[styles.formLabel, { color: themeColors.textMuted }]}>العنوان *</Text>
                  <TextInput
                    value={formTitle}
                    onChangeText={setFormTitle}
                    placeholder="مثال: اجتماع المتابعة الدورية الربعي للربع الأول 2026"
                    placeholderTextColor={themeColors.textMuted}
                    style={[styles.input, { backgroundColor: themeColors.inputBackground, borderColor: themeColors.border, color: themeColors.textPrimary }]}
                    textAlign="right"
                  />
                </View>

                <View style={{ flexDirection: 'row-reverse', gap: 12 }}>
                  <View style={{ flex: 1 }}>
                    <Text style={[styles.formLabel, { color: themeColors.textMuted }]}>نوع الاجتماع *</Text>
                    <View style={[styles.input, { backgroundColor: themeColors.inputBackground, borderColor: themeColors.border, padding: 0 }]}>
                      <select
                        value={formType}
                        onChange={(e) => setFormType(e.target.value as CalibrationMeetingType)}
                        style={{
                          width: '100%',
                          height: 44,
                          padding: '0 12px',
                          background: 'transparent',
                          border: 'none',
                          outline: 'none',
                          color: themeColors.textPrimary,
                          fontFamily: FONTS.regular as any,
                          fontSize: 14,
                          textAlign: 'right',
                          direction: 'rtl',
                        }}
                      >
                        <option value="quarterly">ربعي</option>
                        <option value="half_yearly">نصف سنوي</option>
                        <option value="three_quarter">ثلاثة أرباع</option>
                        <option value="year_end">نهاية الدورة</option>
                      </select>
                    </View>
                  </View>
                </View>

                <View style={{ flexDirection: 'row-reverse', gap: 12 }}>
                  <View style={{ flex: 1 }}>
                    <Text style={[styles.formLabel, { color: themeColors.textMuted }]}>التاريخ *</Text>
                    {/* Native DOM <input> — RN-Web TextInput drops the `type` prop,
                        so date/time pickers never rendered and free-typed values
                        failed validation. Raw inputs emit YYYY-MM-DD / HH:MM. */}
                    <View style={[styles.input, { backgroundColor: themeColors.inputBackground, borderColor: themeColors.border, padding: 0 }]}>
                      <input
                        type="date"
                        value={formDate}
                        onChange={(e) => setFormDate(e.target.value)}
                        style={{
                          width: '100%',
                          height: 44,
                          padding: '0 12px',
                          background: 'transparent',
                          border: 'none',
                          outline: 'none',
                          color: themeColors.textPrimary,
                          fontFamily: FONTS.regular as any,
                          fontSize: 14,
                          textAlign: 'right',
                          direction: 'rtl',
                          boxSizing: 'border-box',
                        }}
                      />
                    </View>
                  </View>
                  <View style={{ flex: 1 }}>
                    <Text style={[styles.formLabel, { color: themeColors.textMuted }]}>الوقت</Text>
                    <View style={[styles.input, { backgroundColor: themeColors.inputBackground, borderColor: themeColors.border, padding: 0 }]}>
                      <input
                        type="time"
                        value={formTime}
                        onChange={(e) => setFormTime(e.target.value)}
                        style={{
                          width: '100%',
                          height: 44,
                          padding: '0 12px',
                          background: 'transparent',
                          border: 'none',
                          outline: 'none',
                          color: themeColors.textPrimary,
                          fontFamily: FONTS.regular as any,
                          fontSize: 14,
                          textAlign: 'right',
                          direction: 'rtl',
                          boxSizing: 'border-box',
                        }}
                      />
                    </View>
                  </View>
                </View>

                <View>
                  <Text style={[styles.formLabel, { color: themeColors.textMuted }]}>الأقسام المدعوة</Text>
                  <MultiSelectDropdown
                    values={formInvitedUnits}
                    options={units.map((u) => ({ value: String(u.id), label: u.name_ar || u.name }))}
                    placeholder="اختر الأقسام المدعوة"
                    onChange={(vals) => setFormInvitedUnits(vals)}
                  />
                </View>

                <View>
                  <Text style={[styles.formLabel, { color: themeColors.textMuted }]}>جدول الأعمال</Text>
                  <TextInput
                    value={formAgenda}
                    onChangeText={setFormAgenda}
                    placeholder="ما الذي سيُغطى في الاجتماع؟"
                    placeholderTextColor={themeColors.textMuted}
                    style={[styles.input, styles.textarea, { backgroundColor: themeColors.inputBackground, borderColor: themeColors.border, color: themeColors.textPrimary }]}
                    textAlign="right"
                    multiline
                  />
                </View>

                <View>
                  <Text style={[styles.formLabel, { color: themeColors.textMuted }]}>ملاحظات الاجتماع</Text>
                  <TextInput
                    value={formNotes}
                    onChangeText={setFormNotes}
                    placeholder="تُملأ خلال أو بعد الاجتماع"
                    placeholderTextColor={themeColors.textMuted}
                    style={[styles.input, styles.textarea, { backgroundColor: themeColors.inputBackground, borderColor: themeColors.border, color: themeColors.textPrimary }]}
                    textAlign="right"
                    multiline
                  />
                </View>

                <View>
                  <Text style={[styles.formLabel, { color: themeColors.textMuted }]}>القرارات المتخذة</Text>
                  <TextInput
                    value={formDecisions}
                    onChangeText={setFormDecisions}
                    placeholder="القرارات الرسمية وتعديلات الخطط"
                    placeholderTextColor={themeColors.textMuted}
                    style={[styles.input, styles.textarea, { backgroundColor: themeColors.inputBackground, borderColor: themeColors.border, color: themeColors.textPrimary }]}
                    textAlign="right"
                    multiline
                  />
                </View>
              </View>

              <View style={{ flexDirection: 'row-reverse', gap: 8, marginTop: 16 }}>
                <TouchableOpacity
                  style={[styles.modalSaveBtn, saving && { opacity: 0.5 }]}
                  disabled={saving}
                  onPress={handleSave}
                >
                  <Text style={styles.modalSaveBtnText}>{saving ? '...' : 'حفظ'}</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.modalCancelBtn, { borderColor: themeColors.border }]}
                  onPress={() => setShowForm(false)}
                >
                  <Text style={[styles.modalCancelBtnText, { color: themeColors.textPrimary }]}>إلغاء</Text>
                </TouchableOpacity>
              </View>
            </ScrollView>
          </View>
        </View>
      </Modal>
    </PageContainer>
  );
}

const styles = StyleSheet.create({
  headerCard: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    gap: 12,
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
  },
  title: { fontSize: 20, fontFamily: FONTS.medium, textAlign: 'right' },
  subtitle: { fontSize: 13, fontFamily: FONTS.regular, textAlign: 'right', marginTop: 4 },
  addButton: {
    paddingVertical: 10, paddingHorizontal: 16,
    backgroundColor: COLORS.primary, borderRadius: 8,
  },
  addButtonText: { color: '#FFFFFF', fontSize: 14, fontFamily: FONTS.medium },
  emptyCard: {
    padding: 40, borderRadius: 12, borderWidth: 1, alignItems: 'center',
  },
  emptyText: { fontSize: 16, fontFamily: FONTS.regular },
  emptyHint: { fontSize: 13, marginTop: 8, fontFamily: FONTS.regular },
  itemCard: {
    flexDirection: 'row-reverse', alignItems: 'flex-start', gap: 12,
    padding: 16, borderRadius: 12, borderWidth: 1,
  },
  itemHeaderRow: {
    flexDirection: 'row-reverse', alignItems: 'center', gap: 8, flexWrap: 'wrap',
  },
  itemTitle: { fontSize: 16, fontFamily: FONTS.medium, textAlign: 'right' },
  itemMeta: { fontSize: 12, fontFamily: FONTS.regular, textAlign: 'right', marginTop: 6 },
  statusBadge: {
    paddingVertical: 3, paddingHorizontal: 10, borderRadius: 6,
  },
  statusBadgeText: { fontSize: 12, fontFamily: FONTS.medium },
  typeBadge: {
    paddingVertical: 3, paddingHorizontal: 10, borderRadius: 6,
    backgroundColor: '#EFF6FF',
  },
  typeBadgeText: { color: '#1E40AF', fontSize: 12, fontFamily: FONTS.medium },
  expandToggle: {
    color: COLORS.primary, fontSize: 13, fontFamily: FONTS.medium,
    marginTop: 8, textAlign: 'right',
  },
  detailBox: {
    marginTop: 8, padding: 12, borderRadius: 8,
    backgroundColor: '#F9FAFB', gap: 10,
  },
  detailGroup: {},
  detailLabel: {
    fontSize: 12, fontFamily: FONTS.medium, color: '#6B7280',
    textAlign: 'right', marginBottom: 4,
  },
  detailText: {
    fontSize: 13, fontFamily: FONTS.regular, textAlign: 'right',
    lineHeight: 20,
  },
  actionsCol: {
    flexDirection: 'column', gap: 6, alignItems: 'stretch', minWidth: 80,
  },
  startBtn: {
    paddingVertical: 6, paddingHorizontal: 12, borderRadius: 6,
    backgroundColor: '#FEF3C7', alignItems: 'center',
  },
  startBtnText: { color: '#92400E', fontSize: 13, fontFamily: FONTS.medium },
  completeBtn: {
    paddingVertical: 6, paddingHorizontal: 12, borderRadius: 6,
    backgroundColor: '#DCFCE7', alignItems: 'center',
  },
  completeBtnText: { color: '#15803D', fontSize: 13, fontFamily: FONTS.medium },
  editBtn: {
    paddingVertical: 6, paddingHorizontal: 12, borderRadius: 6,
    backgroundColor: '#EFF6FF', alignItems: 'center',
  },
  editBtnText: { color: '#1E40AF', fontSize: 13, fontFamily: FONTS.medium },
  cancelMeetingBtn: {
    paddingVertical: 6, paddingHorizontal: 12, borderRadius: 6,
    backgroundColor: '#F3F4F6', alignItems: 'center',
  },
  cancelMeetingBtnText: { color: '#374151', fontSize: 13, fontFamily: FONTS.medium },
  deleteBtn: {
    paddingVertical: 6, paddingHorizontal: 12, borderRadius: 6,
    backgroundColor: '#FEF2F2', alignItems: 'center',
  },
  deleteBtnText: { color: '#B91C1C', fontSize: 13, fontFamily: FONTS.medium },
  modalOverlay: {
    flex: 1, backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center', alignItems: 'center', padding: 16,
  },
  modalCard: {
    width: '100%', maxWidth: 640, maxHeight: '90%',
    padding: 20, borderRadius: 12, borderWidth: 1,
  },
  modalTitle: {
    fontSize: 18, fontFamily: FONTS.medium, textAlign: 'right', marginBottom: 16,
  },
  formLabel: { fontSize: 13, fontFamily: FONTS.regular, textAlign: 'right', marginBottom: 6 },
  input: {
    borderWidth: 1, borderRadius: 8, paddingHorizontal: 12, paddingVertical: 10,
    fontSize: 14, fontFamily: FONTS.regular, minHeight: 44,
  },
  textarea: { minHeight: 90, paddingTop: 10 },
  modalSaveBtn: {
    flex: 1, paddingVertical: 12, backgroundColor: COLORS.primary,
    borderRadius: 8, alignItems: 'center',
  },
  modalSaveBtnText: { color: '#FFFFFF', fontSize: 14, fontFamily: FONTS.medium },
  modalCancelBtn: {
    flex: 1, paddingVertical: 12, borderWidth: 1,
    borderRadius: 8, alignItems: 'center',
  },
  modalCancelBtnText: { fontSize: 14, fontFamily: FONTS.medium },
});
